import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Link } from 'react-router-dom';
import {
    Card,
    CardBody,
    Typography,
    Chip
} from "@material-tailwind/react";
import {
    CalendarIcon,
    ClockIcon,
    CheckCircleIcon,
    ExclamationCircleIcon
} from "@heroicons/react/24/outline";

const TaskCard = ({ task }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ id: task.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1
    };

    const getPriorityColor = (priority) => {
        const colors = {
            low: 'blue-gray',
            normal: 'blue',
            high: 'orange',
            urgent: 'red'
        };
        return colors[priority] || 'gray';
    };

    const formatDate = (dateString) => {
        if (!dateString) return null;
        const date = new Date(dateString);
        const today = new Date();
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);

        if (date.toDateString() === today.toDateString()) {
            return 'Today';
        } else if (date.toDateString() === tomorrow.toDateString()) {
            return 'Tomorrow';
        } else {
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        }
    };

    const isOverdue = (dueDate) => {
        if (!dueDate) return false;
        return new Date(dueDate) < new Date();
    };

    return (
        <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
            <Card className="hover:shadow-lg transition-shadow cursor-grab active:cursor-grabbing bg-white">
                <CardBody className="p-3">
                    <Link to={`/tasks/${task.id}`}>
                        <Typography
                            variant="small"
                            color="blue-gray"
                            className="font-semibold hover:text-[#FF5733] mb-2 line-clamp-2"
                        >
                            {task.title}
                        </Typography>
                    </Link>

                    {task.description && (
                        <Typography
                            variant="small"
                            color="gray"
                            className="text-xs mb-3 line-clamp-2"
                        >
                            {task.description}
                        </Typography>
                    )}

                    <div className="flex flex-wrap gap-2 mb-3">
                        <Chip
                            size="sm"
                            value={task.priority}
                            color={getPriorityColor(task.priority)}
                            className="capitalize text-xs"
                        />
                    </div>

                    <div className="space-y-2">
                        {/* Due Date */}
                        {task.due_date && (
                            <div className={`flex items-center gap-1 text-xs ${isOverdue(task.due_date) ? 'text-red-500' : 'text-gray-600'}`}>
                                <CalendarIcon className="h-3 w-3" />
                                <span>{formatDate(task.due_date)}</span>
                                {isOverdue(task.due_date) && (
                                    <ExclamationCircleIcon className="h-3 w-3" />
                                )}
                            </div>
                        )}

                        {/* Progress Indicators */}
                        <div className="flex items-center gap-3 text-xs text-gray-600">
                            {/* Checklist */}
                            {task.total_checklist_items > 0 && (
                                <div className="flex items-center gap-1">
                                    <CheckCircleIcon className="h-3 w-3" />
                                    <span>{task.completed_checklist_items}/{task.total_checklist_items}</span>
                                </div>
                            )}

                            {/* Comments */}
                            {task.comment_count > 0 && (
                                <div className="flex items-center gap-1">
                                    <span>💬</span>
                                    <span>{task.comment_count}</span>
                                </div>
                            )}

                            {/* Attachments */}
                            {task.attachment_count > 0 && (
                                <div className="flex items-center gap-1">
                                    <span>📎</span>
                                    <span>{task.attachment_count}</span>
                                </div>
                            )}

                            {/* Time Estimate */}
                            {task.estimated_hours && (
                                <div className="flex items-center gap-1">
                                    <ClockIcon className="h-3 w-3" />
                                    <span>{task.estimated_hours}h</span>
                                </div>
                            )}
                        </div>

                        {/* Assignees */}
                        {task.assigned_to && task.assigned_to.length > 0 && (
                            <div className="flex items-center gap-1">
                                {task.assigned_to.slice(0, 3).map((userId, index) => (
                                    <div
                                        key={userId}
                                        className="w-6 h-6 rounded-full bg-[#FF5733] flex items-center justify-center text-white text-xs font-semibold"
                                        style={{ marginLeft: index > 0 ? '-8px' : '0' }}
                                    >
                                        {userId.substring(0, 2).toUpperCase()}
                                    </div>
                                ))}
                                {task.assigned_to.length > 3 && (
                                    <div className="w-6 h-6 rounded-full bg-gray-300 flex items-center justify-center text-gray-700 text-xs font-semibold ml-[-8px]">
                                        +{task.assigned_to.length - 3}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </CardBody>
            </Card>
        </div>
    );
};

export default TaskCard;
